/**
 * useWorkforce — data hooks for the Workforce screens.
 *
 * Every hook hydrates from the declared workforce ops (wf-api.ts → apiRaw).
 * No seed data, no fabricated fallbacks: while the data layer is pending the
 * hooks surface { error } and screens render the honest WfPending state.
 * Mutations are the callers' job (screens await wf-api directly, per the
 * no-fake-interactivity mandate); hooks expose reload() to reconcile.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTenant } from "../../shell/access";

/**
 * 2026-07-27: an `ApiError` from the typed client carries status/code but an
 * EMPTY `message` when the mock envelope has no message (every unhandled
 * 404 in the preview looks like this). Setting that empty string as `error`
 * made `error ? … : loading || !data ? …` fall through to the loading branch,
 * so a 404'd screen sat on "Loading…" forever (Sarah's Floor board). Always
 * produce a non-empty, honest description.
 */
function describeFetchError(e: unknown): string {
  const err = e as { status?: number; code?: string; message?: string } | null;
  const message = typeof err?.message === "string" ? err.message.trim() : "";
  if (message) return message;
  const status = typeof err?.status === "number" ? err.status : null;
  if (status === 404) return "This operation isn't served in the preview yet (404) — the data layer is pending.";
  if (status === 401 || status === 403) return `The signed-in session isn't allowed to read this (${status}).`;
  if (status !== null) return `The request failed with status ${status}${err?.code ? ` (${err.code})` : ""}.`;
  return String(e);
}

export interface WfResource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/** Generic fetch-on-mount hook, re-fetching when `key` changes. */
export function useWfResource<T>(key: string, fetcher: () => Promise<T>, opts?: { pollMs?: number }): WfResource<T> {
  // Workspace-aware: switching tenants (top-left switcher) re-scopes every
  // workforce surface — the fetch key incorporates the active tenant.
  const tenant = useTenant();
  const scopedKey = `${tenant ? tenant.id : ""}§${key}`;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    fetcherRef.current()
      .then((d) => {
        if (!live) return;
        // 2026-07-27: a kit op that isn't mock-served in this preview resolves
        // to null/undefined (404 with an empty body) rather than throwing —
        // which used to leave screens on "Loading…" forever. Treat a nullish
        // payload as the honest pending state, not as loading.
        if (d === null || d === undefined) {
          setError("Data layer pending — this operation isn't served in the preview yet.");
          setLoading(false);
          return;
        }
        setData(d); setLoading(false);
      })
      .catch((e: unknown) => {
        if (!live) return;
        console.warn(`workforce fetch failed (${key}):`, e);
        setError(describeFetchError(e));
        setLoading(false);
      });
    return () => { live = false; };
  }, [scopedKey, nonce]);

  // Optional slow poll (ops board). Silent refresh — keeps last good data on failure.
  useEffect(() => {
    if (!opts?.pollMs) return;
    const t = setInterval(() => {
      // Keep the last good snapshot on a nullish/failed refresh — never blank
      // a loaded board back into the loading state.
      fetcherRef.current().then((d) => { if (d !== null && d !== undefined) setData(d); }).catch(() => { /* keep last snapshot */ });
    }, opts.pollMs);
    return () => clearInterval(t);
  }, [scopedKey, opts?.pollMs]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, loading, error, reload };
}

/** Serialized-busy helper for awaited mutations: exposes busy id + runner. */
export function useBusy() {
  const [busy, setBusy] = useState<string | null>(null);
  const run = useCallback(async (id: string, fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(id);
    try { await fn(); } finally { setBusy(null); }
  }, [busy]);
  return { busy, run };
}
